/* ================================================================== */
/*  KuberNodes AI Authority Types                                     */
/*                                                                    */
/*  The permission-governed autonomous AI operating model.            */
/*  Every AI action is treated like a human engineer action:          */
/*  logged, authorized, reversible.                                   */
/*                                                                    */
/*  The AI is not a chatbot. It is an autonomous DevOps + Smart       */
/*  Contract + Backend engineer that acts inside clearly defined       */
/*  authority boundaries.                                             */
/* ================================================================== */

import type { OrgRole } from './platform';

/* ================================================================== */
/*  AI Authority Tiers                                                */
/* ================================================================== */

/**
 * Tier 1 — Suggestion Mode (default)
 *   AI suggests, user approves everything
 *
 * Tier 2 — Assisted Execution
 *   AI can create/modify files, run tests, prepare deployments
 *   Deployments still require human approval
 *
 * Tier 3 — Autonomous Dev Mode (org-controlled)
 *   AI can deploy contracts, create infra, scale services, fix issues
 *   Within org-defined limits. Requires admin enable.
 *
 * Tier 4 — Full Autonomous Ops (rare, enterprise only)
 *   AI can deploy, scale, optimize cost, patch vulns, rotate keys
 *   Only for trusted orgs with strict policies. Everything logged + reversible.
 */
export type AiAuthorityTier = 1 | 2 | 3 | 4;

export const AI_TIER_NAMES: Record<AiAuthorityTier, string> = {
  1: 'Suggestion',
  2: 'Assisted',
  3: 'Autonomous Dev',
  4: 'Full Autonomous',
};

export const AI_TIER_DESCRIPTIONS: Record<AiAuthorityTier, string> = {
  1: 'AI suggests code, deployments, and fixes. User approves all actions.',
  2: 'AI can create files, modify code, and run tests. Deployments require approval.',
  3: 'AI can deploy contracts, create infra, and fix issues within org-defined limits.',
  4: 'AI operates fully autonomously: deploy, scale, patch, rotate keys. Enterprise only.',
};

/* ================================================================== */
/*  AI Permissions — RBAC for Autonomous Agent                        */
/* ================================================================== */

/**
 * Fine-grained permissions the AI virtual member can hold.
 * Org admin decides which permissions to grant.
 */
export type AiPermission =
  // Workspace
  | 'workspace:read'
  | 'workspace:create'
  | 'workspace:modify'
  | 'workspace:delete'
  // Code
  | 'code:read'
  | 'code:create'
  | 'code:modify'
  | 'code:delete'
  // Build
  | 'build:compile'
  | 'build:test'
  // Deploy
  | 'deploy:testnet'
  | 'deploy:mainnet'
  | 'deploy:approve'
  // Infra
  | 'infra:scale'
  | 'infra:configure'
  // AI capabilities
  | 'ai:generate'
  | 'ai:audit'
  | 'ai:optimize'
  | 'ai:fix'
  // Secrets
  | 'secret:read_ref'
  | 'secret:rotate'
  // RPC
  | 'rpc:use'
  | 'rpc:configure'
  // Monitoring
  | 'monitor:read'
  | 'monitor:configure';

/**
 * Default permission sets per authority tier.
 */
export const TIER_DEFAULT_PERMISSIONS: Record<AiAuthorityTier, AiPermission[]> = {
  1: ['code:read', 'ai:generate', 'ai:audit', 'ai:optimize', 'ai:fix', 'monitor:read'],
  2: [
    'code:read',
    'code:create',
    'code:modify',
    'workspace:read',
    'workspace:modify',
    'build:compile',
    'build:test',
    'ai:generate',
    'ai:audit',
    'ai:optimize',
    'ai:fix',
    'rpc:use',
    'monitor:read',
  ],
  3: [
    'code:read',
    'code:create',
    'code:modify',
    'code:delete',
    'workspace:read',
    'workspace:create',
    'workspace:modify',
    'build:compile',
    'build:test',
    'deploy:testnet',
    'deploy:mainnet',
    'infra:scale',
    'ai:generate',
    'ai:audit',
    'ai:optimize',
    'ai:fix',
    'rpc:use',
    'rpc:configure',
    'secret:read_ref',
    'monitor:read',
    'monitor:configure',
  ],
  4: [
    'code:read',
    'code:create',
    'code:modify',
    'code:delete',
    'workspace:read',
    'workspace:create',
    'workspace:modify',
    'workspace:delete',
    'build:compile',
    'build:test',
    'deploy:testnet',
    'deploy:mainnet',
    'deploy:approve',
    'infra:scale',
    'infra:configure',
    'ai:generate',
    'ai:audit',
    'ai:optimize',
    'ai:fix',
    'rpc:use',
    'rpc:configure',
    'secret:read_ref',
    'secret:rotate',
    'monitor:read',
    'monitor:configure',
  ],
};

/**
 * Minimum org role required to enable each tier.
 */
export const TIER_REQUIRED_ROLE: Record<AiAuthorityTier, OrgRole> = {
  1: 'viewer',
  2: 'developer',
  3: 'admin',
  4: 'owner',
};

/* ================================================================== */
/*  AI Action System                                                  */
/* ================================================================== */

/** Risk level determines approval requirements */
export type ActionRisk = 'low' | 'medium' | 'high' | 'critical';

/** Categories of AI actions */
export type AiActionCategory =
  | 'code' // Create, modify, delete files
  | 'build' // Compile, test
  | 'deploy' // Deploy to chain
  | 'infra' // Scale, configure infrastructure
  | 'security' // Audit, patch vulnerabilities, rotate keys
  | 'optimize' // Gas optimization, cost optimization
  | 'monitor' // Read/configure monitoring
  | 'workspace'; // Workspace lifecycle

/** Specific AI action types extending platform audit actions */
export type AiActionType =
  // Code actions
  | 'ai.code.create'
  | 'ai.code.modify'
  | 'ai.code.delete'
  | 'ai.code.refactor'
  // Build actions
  | 'ai.build.compile'
  | 'ai.build.test'
  | 'ai.build.fix_errors'
  // Deploy actions
  | 'ai.deploy.testnet'
  | 'ai.deploy.mainnet'
  | 'ai.deploy.verify'
  // Infra actions
  | 'ai.infra.scale_up'
  | 'ai.infra.scale_down'
  | 'ai.infra.configure_rpc'
  // Security actions
  | 'ai.security.audit'
  | 'ai.security.patch'
  | 'ai.security.rotate_keys'
  // Optimize actions
  | 'ai.optimize.gas'
  | 'ai.optimize.cost'
  | 'ai.optimize.performance'
  // Workspace actions
  | 'ai.workspace.create'
  | 'ai.workspace.configure'
  | 'ai.workspace.cleanup';

/** Risk classification for each action type */
export const ACTION_RISK_MAP: Record<AiActionType, ActionRisk> = {
  'ai.code.create': 'low',
  'ai.code.modify': 'low',
  'ai.code.delete': 'medium',
  'ai.code.refactor': 'medium',
  'ai.build.compile': 'low',
  'ai.build.test': 'low',
  'ai.build.fix_errors': 'low',
  'ai.deploy.testnet': 'medium',
  'ai.deploy.mainnet': 'critical',
  'ai.deploy.verify': 'low',
  'ai.infra.scale_up': 'medium',
  'ai.infra.scale_down': 'medium',
  'ai.infra.configure_rpc': 'high',
  'ai.security.audit': 'low',
  'ai.security.patch': 'high',
  'ai.security.rotate_keys': 'critical',
  'ai.optimize.gas': 'low',
  'ai.optimize.cost': 'medium',
  'ai.optimize.performance': 'medium',
  'ai.workspace.create': 'low',
  'ai.workspace.configure': 'medium',
  'ai.workspace.cleanup': 'medium',
};

/* ================================================================== */
/*  AI Action Proposal — The Core Object                              */
/* ================================================================== */

export type AiActionStatus =
  | 'proposed' // AI has proposed, awaiting decision
  | 'approved' // User or policy approved
  | 'executing' // Currently running
  | 'completed' // Finished successfully
  | 'failed' // Execution failed
  | 'rejected' // User rejected
  | 'rolled_back' // Was completed but rolled back
  | 'expired'; // Approval window passed

export interface AiAction {
  id: string;
  /** The action type that determines risk and permissions */
  type: AiActionType;
  category: AiActionCategory;
  /** Human-readable summary of what the AI wants to do */
  title: string;
  /** Detailed description / AI reasoning */
  description: string;
  /** Risk level (derived from type) */
  risk: ActionRisk;
  /** Current status */
  status: AiActionStatus;
  /** Required permissions for this action */
  requiredPermissions: AiPermission[];

  /* -- Context ---------------------------------------------------- */
  /** Project this action affects */
  projectId?: string;
  /** Workspace this action affects */
  workspaceId?: string;
  /** Specific files affected */
  affectedFiles?: string[];
  /** Chain / environment for deploy actions */
  chainId?: number;
  /** Estimated cost impact */
  estimatedCost?: { gas?: number; usd?: number };

  /* -- Execution details ------------------------------------------ */
  /** What the AI will actually execute (serializable action plan) */
  plan: AiActionStep[];
  /** Result after execution */
  result?: AiActionResult;
  /** Rollback info if action is reversible */
  rollback?: AiRollbackInfo;

  /* -- Approval --------------------------------------------------- */
  /** Whether this action can auto-execute based on tier + policy */
  autoApproved: boolean;
  /** Who approved (null if auto or pending) */
  approvedBy?: string;
  /** Approval/rejection reason */
  approvalNote?: string;

  /* -- Timestamps ------------------------------------------------- */
  proposedAt: string;
  approvedAt?: string;
  executedAt?: string;
  completedAt?: string;
  expiresAt?: string;
}

export interface AiActionStep {
  id: string;
  order: number;
  /** What this step does */
  description: string;
  /** Service to call */
  service: 'workspace' | 'deploy' | 'ai' | 'file' | 'rpc' | 'infra' | 'secret';
  /** Service method */
  method: string;
  /** Parameters */
  params: Record<string, unknown>;
  /** Step status */
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  /** Step result */
  result?: unknown;
  /** Error if failed */
  error?: string;
}

export interface AiActionResult {
  success: boolean;
  /** Human-readable outcome */
  summary: string;
  /** Any outputs (contract addresses, file paths, etc.) */
  outputs: Record<string, unknown>;
  /** Errors if failed */
  errors?: string[];
  /** Duration in ms */
  durationMs: number;
}

export interface AiRollbackInfo {
  /** Whether this action can be rolled back */
  canRollback: boolean;
  /** Steps to undo the action */
  steps?: AiActionStep[];
  /** Expiry for rollback availability */
  expiresAt?: string;
}

/* ================================================================== */
/*  AI Agent Policy — Org-Level Configuration                         */
/* ================================================================== */

export interface AiAgentPolicy {
  /** Authority tier for this org */
  tier: AiAuthorityTier;
  /** Explicit permission overrides (add or remove from tier defaults) */
  permissionOverrides?: {
    grant?: AiPermission[];
    deny?: AiPermission[];
  };

  /* -- Guardrails ------------------------------------------------- */
  /** Max actions per hour (rate limit) */
  maxActionsPerHour: number;
  /** Max cost per action in USD */
  maxCostPerAction: number;
  /** Max total AI spend per day in USD */
  maxDailyBudget: number;
  /** Risk levels that require manual approval regardless of tier */
  requireApprovalFor: ActionRisk[];
  /** Action types that are always blocked */
  blockedActions: AiActionType[];
  /** Whether AI can deploy to mainnet */
  allowMainnetDeploy: boolean;
  /** Whether AI can modify secrets */
  allowSecretAccess: boolean;
  /** Time window for auto-rollback eligibility (minutes) */
  rollbackWindowMinutes: number;
  /** Whether to notify on every AI action */
  notifyOnAction: boolean;
  /** Whether the AI kill switch is engaged */
  killSwitch: boolean;
}

export const DEFAULT_AI_POLICY: AiAgentPolicy = {
  tier: 1,
  maxActionsPerHour: 30,
  maxCostPerAction: 0.5,
  maxDailyBudget: 10.0,
  requireApprovalFor: ['high', 'critical'],
  blockedActions: [],
  allowMainnetDeploy: false,
  allowSecretAccess: false,
  rollbackWindowMinutes: 60,
  notifyOnAction: true,
  killSwitch: false,
};

/* ================================================================== */
/*  AI Agent Runtime State                                            */
/* ================================================================== */

export interface AiAgentStatus {
  /** Whether the agent is active and processing */
  isActive: boolean;
  /** Current authority tier */
  tier: AiAuthorityTier;
  /** Effective permissions (tier defaults + overrides) */
  permissions: AiPermission[];
  /** Actions performed this hour */
  actionsThisHour: number;
  /** Budget spent today */
  spentToday: number;
  /** Whether the kill switch is engaged */
  killSwitchEngaged: boolean;
  /** Currently executing action */
  currentAction: AiAction | null;
  /** Last heartbeat from the agent */
  lastHeartbeat: string;
}

/* ================================================================== */
/*  AI Workflow Templates                                              */
/* ================================================================== */

/**
 * Pre-defined workflows the AI can execute as compound actions.
 * Each workflow is a sequence of AiActionTypes with parameters.
 */
export interface AiWorkflow {
  id: string;
  name: string;
  description: string;
  /** The sequence of actions this workflow performs */
  actions: AiActionType[];
  /** Minimum tier required */
  minTier: AiAuthorityTier;
  /** Overall risk (highest risk among constituent actions) */
  risk: ActionRisk;
  /** Estimated duration */
  estimatedDurationMs: number;
  /** Category */
  category: 'scaffold' | 'deploy' | 'audit' | 'optimize' | 'maintain';
}

export const BUILT_IN_WORKFLOWS: AiWorkflow[] = [
  {
    id: 'wf-scaffold-contract',
    name: 'Scaffold & Compile',
    description:
      'Generate a smart contract from prompt, create project files, compile and run tests.',
    actions: ['ai.code.create', 'ai.build.compile', 'ai.build.test'],
    minTier: 2,
    risk: 'low',
    estimatedDurationMs: 30_000,
    category: 'scaffold',
  },
  {
    id: 'wf-full-deploy',
    name: 'Build & Deploy',
    description: 'Compile, audit, optimize gas, deploy to selected chain, and verify on explorer.',
    actions: [
      'ai.build.compile',
      'ai.security.audit',
      'ai.optimize.gas',
      'ai.deploy.testnet',
      'ai.deploy.verify',
    ],
    minTier: 3,
    risk: 'medium',
    estimatedDurationMs: 120_000,
    category: 'deploy',
  },
  {
    id: 'wf-security-sweep',
    name: 'Security Sweep',
    description: 'Full security audit, patch vulnerabilities, re-compile, and verify fixes.',
    actions: ['ai.security.audit', 'ai.security.patch', 'ai.build.compile', 'ai.build.test'],
    minTier: 3,
    risk: 'high',
    estimatedDurationMs: 60_000,
    category: 'audit',
  },
  {
    id: 'wf-gas-optimize',
    name: 'Gas Optimization',
    description: 'Analyze gas usage, refactor code for savings, compile, and verify correctness.',
    actions: ['ai.optimize.gas', 'ai.code.refactor', 'ai.build.compile', 'ai.build.test'],
    minTier: 2,
    risk: 'medium',
    estimatedDurationMs: 45_000,
    category: 'optimize',
  },
  {
    id: 'wf-mainnet-deploy',
    name: 'Production Deploy',
    description:
      'Full pipeline: audit, optimize, deploy to mainnet, verify. Critical risk — requires explicit approval.',
    actions: ['ai.security.audit', 'ai.optimize.gas', 'ai.deploy.mainnet', 'ai.deploy.verify'],
    minTier: 4,
    risk: 'critical',
    estimatedDurationMs: 180_000,
    category: 'deploy',
  },
];
